import {
  AlertCircle,
  Check,
  CloudRain,
  FileAudio,
  Flame,
  Heart,
  Leaf,
  Loader2,
  Music,
  Sparkles,
  Star,
  Sun,
  Upload,
  Volume2,
  Zap,
} from "lucide-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { ref, uploadBytesResumable } from "firebase/storage";
import { useAuth } from "../contexts/AuthContext";
import { cn } from "../lib/utils";
import { storage } from "../services/Firebase";

const MAX_AUDIO_FILE_SIZE = 50 * 1024 * 1024;
const SUPPORTED_AUDIO_EXTENSIONS = new Set([
  "aac",
  "flac",
  "m4a",
  "mp3",
  "mp4",
  "ogg",
  "wav",
  "webm",
]);

const AUDIO_CONTENT_TYPES: Record<string, string> = {
  aac: "audio/aac",
  flac: "audio/flac",
  m4a: "audio/mp4",
  mp3: "audio/mpeg",
  mp4: "audio/mp4",
  ogg: "audio/ogg",
  wav: "audio/wav",
  webm: "audio/webm",
};

const MOOD_ICONS: Record<string, React.FC<{ className?: string }>> = {
  happy: Sun, upbeat: Zap, excited: Flame, dynamic: Zap, bright: Sparkles,
  calm: Leaf, chill: Leaf, relaxed: Leaf, peaceful: Leaf,
  sad: CloudRain, lonely: CloudRain, sorrow: CloudRain, nostalgic: CloudRain,
  inspirational: Star, hopeful: Star, uplifting: Star, love: Heart,
  serious: Zap, angry: Flame, playful: Sparkles, carefree: Sun,
};

function getMoodIcon(moods: string[]): React.FC<{ className?: string }> {
  for (const mood of moods) {
    const Icon = MOOD_ICONS[mood.toLowerCase()];
    if (Icon) return Icon;
  }
  return Music;
}

interface MusicTrack {
  id: string;
  name: string;
  path: string;
  genre: string[];
  mood: string[];
}

type MusicLibraryUser = {
  getIdToken: () => Promise<string>;
};

let cachedMusicLibrary: MusicTrack[] | null = null;
let pendingMusicLibraryRequest: Promise<MusicTrack[]> | null = null;

export function prefetchMusicLibrary(
  currentUser: MusicLibraryUser
): Promise<MusicTrack[]> {
  if (cachedMusicLibrary) return Promise.resolve(cachedMusicLibrary);
  if (pendingMusicLibraryRequest) return pendingMusicLibraryRequest;

  const request = (async () => {
    const token = await currentUser.getIdToken();
    const response = await fetch(
      `${process.env.REACT_APP_API_BASE}/carousel-video/music-library`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (!response.ok) {
      throw new Error(`Failed to load music library (${response.status})`);
    }

    const data = await response.json();
    if (!Array.isArray(data.tracks)) {
      throw new Error("Music library returned an invalid response");
    }

    const tracks = data.tracks as MusicTrack[];
    cachedMusicLibrary = tracks;
    return tracks;
  })();

  pendingMusicLibraryRequest = request;
  const clearPendingRequest = () => {
    if (pendingMusicLibraryRequest === request) {
      pendingMusicLibraryRequest = null;
    }
  };
  void request.then(clearPendingRequest, clearPendingRequest);
  return request;
}

interface MusicPickerProps {
  selectedMusicPath?: string;
  postId?: string;
  disabled?: boolean;
  onSelectTrack: (path: string, name: string) => void | Promise<void>;
}

export function MusicPicker({
  selectedMusicPath,
  postId,
  disabled = false,
  onSelectTrack,
}: MusicPickerProps) {
  const { currentUser, activeWorkspaceId } = useAuth();
  const [tracks, setTracks] = useState<MusicTrack[]>(
    () => cachedMusicLibrary ?? []
  );
  const [isLoading, setIsLoading] = useState(cachedMusicLibrary === null);
  const [libraryError, setLibraryError] = useState<string | null>(null);
  const [libraryRetry, setLibraryRetry] = useState(0);
  const [uploadedTrack, setUploadedTrack] = useState<MusicTrack | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const mountedRef = useRef(true);
  const selectionContextRef = useRef({ postId, disabled });
  const isUploading = uploadProgress !== null;

  useEffect(() => {
    selectionContextRef.current = { postId, disabled };
  }, [disabled, postId]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // The library is identical for every picker. Reuse the same request and
  // session cache so opening a second panel never repeats the auth round-trip.
  useEffect(() => {
    if (!currentUser) {
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    if (!cachedMusicLibrary) setIsLoading(true);
    setLibraryError(null);

    (async () => {
      try {
        const nextTracks = await prefetchMusicLibrary(currentUser);
        if (!cancelled) {
          setTracks(nextTracks);
        }
      } catch (error) {
        console.error("Failed to fetch music library:", error);
        if (!cancelled) {
          setLibraryError("Couldn't load tracks.");
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [currentUser, libraryRetry]);

  const handleSelect = useCallback((track: MusicTrack) => {
    onSelectTrack(track.path, track.name);
  }, [onSelectTrack]);

  const getFileExtension = useCallback((file: File) => {
    const extension = file.name.split(".").pop()?.toLowerCase();
    if (extension && SUPPORTED_AUDIO_EXTENSIONS.has(extension)) {
      return extension;
    }

    const subtype = file.type.split("/")[1]?.split(";")[0]?.toLowerCase();
    if (subtype === "mpeg") return "mp3";
    if (subtype && SUPPORTED_AUDIO_EXTENSIONS.has(subtype)) return subtype;
    return null;
  }, []);

  const getDisplayName = useCallback((fileName: string) => {
    return (
      fileName
        .replace(/\.[^.]+$/, "")
        .replace(/[-_]+/g, " ")
        .trim() || "Custom audio"
    );
  }, []);

  const getSafeBaseName = useCallback((fileName: string) => {
    return (
      fileName
        .replace(/\.[^.]+$/, "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "")
        .slice(0, 48) || "audio"
    );
  }, []);

  const getTrackNameFromPath = useCallback((path: string) => {
    const fileName = path.split("/").pop() || "Custom audio";
    return getDisplayName(fileName.replace(/^\d+-/, ""));
  }, [getDisplayName]);

  const handleAudioUpload = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file || disabled) return;

      setUploadError(null);

      const extension = getFileExtension(file);
      if (!extension || (file.type && !file.type.startsWith("audio/"))) {
        setUploadError("Choose an audio file.");
        event.target.value = "";
        return;
      }

      if (file.size > MAX_AUDIO_FILE_SIZE) {
        setUploadError("Audio must be 50MB or smaller.");
        event.target.value = "";
        return;
      }

      if (!currentUser) {
        setUploadError("Sign in to upload audio.");
        event.target.value = "";
        return;
      }

      const safeName = getSafeBaseName(file.name);
      const folder = postId
        ? `posts/${activeWorkspaceId}/${postId}/music`
        : `posts/${activeWorkspaceId}/music`;
      const storagePath = `${folder}/${Date.now()}-${safeName}.${extension}`;
      const storageRef = ref(storage, storagePath);
      const uploadTask = uploadBytesResumable(storageRef, file, {
        contentType: file.type || AUDIO_CONTENT_TYPES[extension],
      });

      setUploadProgress(0);

      uploadTask.on(
        "state_changed",
        (snapshot) => {
          if (!mountedRef.current) return;
          const progress = Math.round(
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100
          );
          setUploadProgress(progress);
        },
        (error) => {
          if (!mountedRef.current) return;
          console.error("Audio upload failed:", error);
          setUploadError("Upload failed. Please try again.");
          setUploadProgress(null);
          event.target.value = "";
        },
        async () => {
          if (!mountedRef.current) return;
          const latestContext = selectionContextRef.current;
          if (latestContext.disabled || latestContext.postId !== postId) {
            setUploadProgress(null);
            setUploadError(
              "Audio uploaded, but wasn't selected because the post changed."
            );
            return;
          }

          const name = getDisplayName(file.name);
          const customTrack = {
            id: storagePath,
            name,
            path: storagePath,
            genre: ["custom"],
            mood: ["custom"],
          };

          setUploadedTrack(customTrack);
          setUploadProgress(null);
          event.target.value = "";
          try {
            await onSelectTrack(storagePath, name);
          } catch (error) {
            console.error("Failed to select uploaded audio:", error);
            setUploadError("Audio uploaded, but the selection was not saved.");
          }
        }
      );
    },
    [
      currentUser,
      activeWorkspaceId,
      disabled,
      getDisplayName,
      getFileExtension,
      getSafeBaseName,
      onSelectTrack,
      postId,
    ]
  );

  // Find current track name
  const currentTrack =
    tracks.find((t) => t.path === selectedMusicPath) ||
    (uploadedTrack?.path === selectedMusicPath ? uploadedTrack : null) ||
    (selectedMusicPath
      ? {
          id: selectedMusicPath,
          name: getTrackNameFromPath(selectedMusicPath),
          path: selectedMusicPath,
          genre: [],
          mood: ["custom"],
        }
      : null);
  const isCustomSelection =
    !!currentTrack && !currentTrack.path.startsWith("music_library/");

  return (
    <div className="space-y-3">
      {currentTrack && (
        <div className="rounded-md border border-primary/20 bg-primary/5 px-3 py-3 text-sm">
          <div className="flex items-start gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
              <Volume2 className="h-4 w-4" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5">
                <span className="text-[11px] font-medium uppercase text-muted-foreground">
                  Selected track
                </span>
                {isCustomSelection && (
                  <span className="shrink-0 rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
                    Custom
                  </span>
                )}
              </div>
              <div className="mt-0.5 truncate font-medium text-foreground">
                {currentTrack.name}
              </div>
            </div>
          </div>
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="audio/*,.aac,.flac,.m4a,.mp3,.mp4,.ogg,.wav,.webm"
        className="hidden"
        onChange={handleAudioUpload}
      />
      <button
        type="button"
        disabled={!currentUser || isUploading}
        onClick={() => fileInputRef.current?.click()}
        className="group flex min-h-12 w-full items-center gap-3 rounded-md border-2 border-dashed border-muted-foreground/25 bg-muted/20 px-3 py-2.5 text-left text-sm transition-colors hover:border-primary/40 hover:bg-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
      >
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-background text-muted-foreground transition-colors group-hover:text-primary">
          {isUploading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Upload className="h-4 w-4" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="truncate font-medium text-foreground">
            {isUploading ? `Uploading ${uploadProgress ?? 0}%` : "Upload audio"}
          </div>
          <div className="truncate text-xs text-muted-foreground">
            MP3, WAV, M4A, OGG up to 50MB
          </div>
        </div>
      </button>
      {isUploading && (
        <div className="h-1 overflow-hidden rounded-full bg-muted">
          <div
            className="h-full rounded-full bg-primary transition-all"
            style={{ width: `${uploadProgress ?? 0}%` }}
          />
        </div>
      )}
      {uploadError && (
        <div className="flex items-center gap-2 text-xs text-destructive">
          <AlertCircle className="h-3.5 w-3.5 shrink-0" />
          <span>{uploadError}</span>
        </div>
      )}

      <div className="overflow-hidden rounded-md border bg-background">
        <div className="flex items-center justify-between border-b px-3 py-2">
          <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
            <FileAudio className="h-3.5 w-3.5" />
            Music library
          </div>
          {tracks.length > 0 && (
            <span className="text-xs tabular-nums text-muted-foreground">
              {tracks.length}
            </span>
          )}
        </div>
        <div className="space-y-0.5 max-h-[42vh] overflow-y-auto p-1">
          {isLoading && tracks.length === 0 ? (
            <div className="flex items-center justify-center gap-2 px-3 py-6 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading tracks...
            </div>
          ) : libraryError && tracks.length === 0 ? (
            <div className="flex flex-col items-center gap-3 px-3 py-5 text-center">
              <div className="flex items-center gap-2 text-sm text-destructive">
                <AlertCircle className="h-4 w-4 shrink-0" />
                <span>{libraryError}</span>
              </div>
              <button
                type="button"
                className="min-h-11 rounded-md border px-4 text-sm font-medium transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                onClick={() => setLibraryRetry((value) => value + 1)}
              >
                Try again
              </button>
            </div>
          ) : (
            tracks.map((track) => {
              const isSelected = track.path === selectedMusicPath;
              const Icon = getMoodIcon(track.mood);

              return (
                <button
                  key={track.id}
                  type="button"
                  className={cn(
                    "flex min-h-11 w-full items-center gap-2 rounded-md px-2 py-2 text-left text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    isSelected
                      ? "bg-primary/10 text-primary"
                      : "text-foreground hover:bg-muted"
                  )}
                  onClick={() => handleSelect(track)}
                >
                  <Icon
                    className={cn(
                      "h-3.5 w-3.5 shrink-0",
                      isSelected ? "text-primary" : "text-muted-foreground"
                    )}
                  />
                  <span className="truncate flex-1">{track.name}</span>
                  {isSelected && <Check className="h-3.5 w-3.5 shrink-0" />}
                </button>
              );
            })
          )}
          {!isLoading && !libraryError && tracks.length === 0 && (
            <div className="px-3 py-6 text-center text-sm text-muted-foreground">
              No tracks available
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
